import { unref } from 'vue';
import { useMutation, UseMutationConfig } from '@libs/use';

import * as API from '../requests';
import { ORDER_SIDES } from '../constants';

export type CreateOrderFormModel = {
  quantity: number;
  price: number;
  totalValue: number;
  executedQuantity?: number;
  validityDate?: Date;
  validityType?: number;
};

type OrderFactoryOptions = {
  formModel: CreateOrderFormModel;
  symbol: string;
  type: 'buy' | 'sell';
  orderExecutionType: string;
  deviceType: number;
};

function orderFactory({
  formModel,
  symbol,
  type,
  deviceType,
  orderExecutionType,
}: OrderFactoryOptions) {
  return {
    orderExecutionType,
    parentId: 0,
    instrumentIdentification: symbol,
    popupSelectionMethod: 1,
    accountRouteType: 1 as const,
    orderSide: ORDER_SIDES[type],
    investorBourseCodeId: 0,
    validateAssetOnSell: false,
    deviceType,
    ...formModel,
  };
}

type UseCreateOrderOptions = {
  symbol: string;
  deviceType: number;
  type: OrderFactoryOptions['type'];
  orderExecutionType: string;
  onReject?: UseMutationConfig<any>['onReject'];
  onSuccess?: UseMutationConfig<any>['onSuccess'];
};

export default function useCreateOrderQuery({
  symbol,
  type,
  orderExecutionType,
  onReject,
  deviceType,
  onSuccess,
}: UseCreateOrderOptions) {
  const {
    execute: createBasketOrder,
    isPending: isLoadingCreateBasketOrder,
    ...query
  } = useMutation(
    ({ formModel }: { formModel: CreateOrderFormModel }) =>
      API.createBasketOrder(
        orderFactory({
          formModel,
          symbol: unref(symbol),
          type,
          deviceType,
          orderExecutionType,
        })
      ),
    {
      onSuccess,
      onReject,
    }
  );

  return { createBasketOrder, isLoadingCreateBasketOrder, ...query };
}
